import { useAuth } from '@clerk/clerk-react';

/**
 * Discriminated on `status` so `principalKey` is non-null exactly when
 * `status === 'ready'` — the impossible state (ready with no key) is
 * unrepresentable rather than merely undocumented.
 */
export type AuthPrincipal =
	| { status: 'resolving'; principalKey: null }
	| { status: 'signed-out'; principalKey: null }
	| { status: 'ready'; principalKey: string };

export type AuthPrincipalStatus = AuthPrincipal['status'];

/**
 * The single source of truth for "is the auth principal resolved".
 *
 * Wraps Clerk's `useAuth()` and collapses its loading/signed-in state into
 * exactly one of three statuses. Consumers must not re-derive readiness from
 * `useAuth()` directly (e.g. checking `isSignedIn` before confirming
 * `isLoaded`) — `isSignedIn` can read stale/true while Clerk is still
 * resolving, which this hook guards against by always checking `isLoaded`
 * first.
 */
export function useAuthPrincipal(): AuthPrincipal {
	const { isLoaded, isSignedIn, sessionId, userId } = useAuth();

	if (!isLoaded) {
		return { status: 'resolving', principalKey: null };
	}

	if (!isSignedIn) {
		return { status: 'signed-out', principalKey: null };
	}

	const principalKey = sessionId ?? userId;
	if (principalKey == null) {
		// Signed in, but Clerk hasn't populated an identifier yet — treat as
		// still resolving rather than fabricate a principal key.
		return { status: 'resolving', principalKey: null };
	}

	return { status: 'ready', principalKey };
}
